import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  crmService,
  CrmLead,
  isoToDatetimeLocalValue,
  datetimeLocalToIso,
  normalizeCrmLeadStatus,
} from '../services/crmService';
import { leadSourceBadgeClass } from '../utils/statusStyles';
import { formatPhoneDisplay } from '../utils/phoneFormat';
import { LEAD_WORKFLOW_STATUS_OPTIONS } from '../components/crm/constants';
import { LeadStatusBadge } from '../components/crm/LeadStatusBadge';
import ContactEmailHistory from '../components/ContactEmailHistory';
import SequenceCard from '../components/sequences/SequenceCard';
import EmailComposeModal from '../components/EmailComposeModal';
import EmailSuggestionModal from '../components/EmailSuggestionModal';
import { OutboundEmailRow } from '../services/emailService';
import { followUpDatetimeLocalOrDefault } from '../utils/followUpDefaults';
import { nextBestActionForInboundLead } from '../utils/nextBestLeadAction';
import { DncBadge } from '../components/crm/DncBadge';
import ScoreBreakdown from '../components/ScoreBreakdown';
import { apiClient } from '../services/apiClient';

/**
 * Full-page inbound marketing lead (`crm.Lead`) — searchable list links here via row click.
 */
const LeadInboundDetail: React.FC = () => {
  const { leadId } = useParams<{ leadId: string }>();
  const navigate = useNavigate();
  const [lead, setLead] = useState<CrmLead | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notesDraft, setNotesDraft] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [nextFollowUpLocal, setNextFollowUpLocal] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [editScheduledEmail, setEditScheduledEmail] = useState<OutboundEmailRow | null>(null);
  const [showEmailSuggestionModal, setShowEmailSuggestionModal] = useState(false);
  const [emailRefreshKey, setEmailRefreshKey] = useState(0);
  const [imageSources, setImageSources] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    if (!leadId) return;
    try {
      setLoading(true);
      setError('');
      const data = await crmService.getMarketingLead(leadId);
      setLead(data);
      setNotesDraft(data.notes ?? '');
      setNextFollowUpLocal(followUpDatetimeLocalOrDefault(data.next_follow_up_at));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load lead');
      setLead(null);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (lead) {
      setNextFollowUpLocal(followUpDatetimeLocalOrDefault(lead.next_follow_up_at));
    }
  }, [lead?.id, lead?.next_follow_up_at]);

  useEffect(() => {
    const entries = lead?.images ?? [];
    const cleanupUrls: string[] = [];
    let cancelled = false;

    const toApiPath = (value: string) => {
      const normalizeApiPath = (candidate: string) => {
        const withQuery = candidate || '';
        const withoutApiPrefix = withQuery.replace(/^\/api(?=\/|$)/, '');
        return withoutApiPrefix.startsWith('/') ? withoutApiPrefix : `/${withoutApiPrefix}`;
      };
      try {
        const parsed = new URL(value);
        return normalizeApiPath(`${parsed.pathname}${parsed.search}`);
      } catch {
        return normalizeApiPath(value);
      }
    };

    if (!entries.length) {
      setImageSources({});
      return () => undefined;
    }

    (async () => {
      const nextSources: Record<string, string> = {};
      for (const image of entries) {
        if (!image.image_url) continue;
        try {
          const blob = await apiClient.fetchAuthorizedBlob(toApiPath(image.image_url));
          if (cancelled) return;
          const objectUrl = URL.createObjectURL(blob);
          cleanupUrls.push(objectUrl);
          nextSources[image.id] = objectUrl;
        } catch {
          // Keep partial success behavior; missing files should not break the page.
        }
      }
      if (!cancelled) setImageSources(nextSources);
    })();

    return () => {
      cancelled = true;
      cleanupUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [lead?.images]);

  const handleStatusChange = async (status: string) => {
    if (!lead) return;
    try {
      const payload: Parameters<typeof crmService.patchMarketingLead>[1] = { status };
      if (status === 'contacted' || status === 'replied') {
        payload.last_contacted = new Date().toISOString();
      }
      const updated = await crmService.patchMarketingLead(lead.id, payload);
      setLead(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update status');
    }
  };

  const handleSaveNotes = async () => {
    if (!lead) return;
    try {
      setSavingNotes(true);
      const updated = await crmService.patchMarketingLead(lead.id, { notes: notesDraft });
      setLead(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleNextFollowUpBlur = async () => {
    if (!lead) return;
    try {
      const next_follow_up_at = datetimeLocalToIso(nextFollowUpLocal);
      const updated = await crmService.patchMarketingLead(lead.id, { next_follow_up_at });
      setLead(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update follow-up');
    }
  };

  const handleConvert = async () => {
    if (!lead || lead.converted_to_client) return;
    try {
      await crmService.convertMarketingLead(lead.id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to convert');
    }
  };

  if (loading && !lead) {
    return (
      <div className="text-fg-muted py-12 text-center">Loading lead…</div>
    );
  }

  if (!lead) {
    return (
      <div className="space-y-4">
        <p className="text-danger-fg">{error || 'Lead not found.'}</p>
        <Link to="/leads?tab=inbound" className="text-primary font-medium hover:underline">
          Back to leads
        </Link>
      </div>
    );
  }

  const converted = !!lead.converted_to_client;
  const tempDisplay = lead.temperature_from_score ?? lead.lead_temperature ?? 'warm';

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => navigate('/leads?tab=inbound')}
          className="text-sm font-medium text-primary hover:underline"
        >
          ← Back to inbound leads
        </button>
      </div>

      {error ? <div className="alert-danger rounded-xl px-4 py-3">{error}</div> : null}

      <div className="rounded-2xl border border-border bg-surface p-6 shadow-sm">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-2 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold text-fg">{lead.full_name}</h1>
              {lead.do_not_contact ? <DncBadge /> : null}
            </div>
            <p className="text-fg-muted">{lead.email}</p>
            {lead.do_not_contact ? (
              <div className="rounded-lg border border-amber-400/50 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-sm space-y-1">
                <p className="font-medium text-amber-950 dark:text-amber-50">Suppressed from campaigns</p>
                {lead.unsubscribed_at ? (
                  <p className="text-xs text-amber-900/90 dark:text-amber-100/85">
                    Unsubscribed: {new Date(lead.unsubscribed_at).toLocaleString()}
                  </p>
                ) : null}
                {lead.unsubscribe_source ? (
                  <p className="text-xs text-amber-900/90 dark:text-amber-100/85">
                    Source: {lead.unsubscribe_source}
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
          <LeadStatusBadge status={lead.status} converted={converted} />
        </div>

        <dl className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 text-sm">
          <div>
            <dt className="text-fg-muted">Company</dt>
            <dd className="text-fg">{lead.company || '—'}</dd>
          </div>
          <div>
            <dt className="text-fg-muted">Phone</dt>
            <dd className="text-fg">{formatPhoneDisplay(lead.phone)}</dd>
          </div>
          <div>
            <dt className="text-fg-muted">Source</dt>
            <dd>
              {lead.source ? (
                <span className={leadSourceBadgeClass(lead.source)}>{lead.source}</span>
              ) : (
                '—'
              )}
            </dd>
          </div>
          {lead.source_details ? (
            <div className="sm:col-span-2">
              <dt className="text-fg-muted">Source details</dt>
              <dd className="text-fg">{lead.source_details}</dd>
            </div>
          ) : null}
          {lead.source_type ? (
            <div>
              <dt className="text-fg-muted">Source type</dt>
              <dd className="text-fg">{lead.source_type}</dd>
            </div>
          ) : null}
          {lead.attribution && Object.keys(lead.attribution).length > 0 ? (
            <div className="sm:col-span-2">
              <dt className="text-fg-muted">Campaign &amp; UTM</dt>
              <dd className="text-fg">
                <ul className="list-inside list-disc space-y-0.5 text-sm">
                  {Object.entries(lead.attribution).map(([k, v]) => (
                    <li key={k}>
                      <span className="font-medium">{k}:</span> {String(v)}
                    </li>
                  ))}
                </ul>
              </dd>
            </div>
          ) : null}
          <div className="sm:col-span-2">
            <dt className="text-fg-muted mb-2">Opportunity score</dt>
            <dd>
              <ScoreBreakdown
                score={lead.score ?? 0}
                signals={lead.opportunity_signals}
                expanded
              />
            </dd>
          </div>
          <div>
            <dt className="text-fg-muted">Temperature (from score)</dt>
            <dd className="font-medium uppercase text-fg">{String(tempDisplay)}</dd>
          </div>
          <div>
            <dt className="text-fg-muted">Status</dt>
            <dd>
              <select
                disabled={converted}
                value={normalizeCrmLeadStatus(lead.status)}
                onChange={(e) => handleStatusChange(e.target.value)}
                className="input-field mt-1 w-full max-w-xs rounded-lg border border-border bg-input px-3 py-2 text-sm text-fg disabled:opacity-50"
              >
                {LEAD_WORKFLOW_STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </dd>
          </div>
          <div>
            <dt className="text-fg-muted">Last contacted</dt>
            <dd className="text-fg">
              {lead.last_contacted ? new Date(lead.last_contacted).toLocaleString() : '—'}
            </dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-fg-muted">Next follow-up</dt>
            <dd>
              <input
                type="datetime-local"
                disabled={converted}
                value={nextFollowUpLocal}
                onChange={(e) => setNextFollowUpLocal(e.target.value)}
                onBlur={handleNextFollowUpBlur}
                className="input-field mt-1 w-full max-w-md rounded-lg border border-border bg-input px-3 py-2 text-sm text-fg disabled:opacity-50"
              />
            </dd>
          </div>
        </dl>

        {lead.message ? (
          <div className="mt-6 border-t border-border pt-4">
            <h2 className="text-sm font-semibold text-fg">Message</h2>
            <p className="mt-1 whitespace-pre-wrap text-sm text-fg">{lead.message}</p>
          </div>
        ) : null}

        {lead.images && lead.images.length > 0 ? (
          <div className="mt-6 border-t border-border pt-4">
            <h2 className="text-sm font-semibold text-fg">Images / Media</h2>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {lead.images.map((image) => {
                const src = imageSources[image.id];
                return (
                  <div key={image.id} className="rounded-xl border border-border bg-surface-muted/30 p-3 space-y-2">
                    <a href={src || undefined} target="_blank" rel="noreferrer" className="block">
                      {src ? (
                        <img src={src} alt={image.caption || 'Lead image'} className="h-40 w-full rounded-lg object-cover" />
                      ) : (
                        <div className="h-40 w-full animate-pulse rounded-lg bg-surface-muted" />
                      )}
                    </a>
                    <p className="text-xs text-fg-muted">
                      Uploaded: {new Date(image.uploaded_at).toLocaleString()}
                    </p>
                    {image.caption ? <p className="text-sm text-fg">{image.caption}</p> : null}
                    {src ? (
                      <a href={src} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">
                        Open full image
                      </a>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        <div className="mt-6 rounded-lg border border-border bg-surface-muted/40 p-4 text-sm text-fg">
          <span className="font-medium text-fg-secondary">Suggested next step: </span>
          {nextBestActionForInboundLead(lead)}
        </div>

        <div className="mt-6 border-t border-border pt-4">
          <h2 className="text-sm font-semibold text-fg">Notes</h2>
          <textarea
            value={notesDraft}
            onChange={(e) => setNotesDraft(e.target.value)}
            disabled={converted}
            rows={4}
            className="input-field mt-2 w-full rounded-lg border border-border bg-input px-3 py-2 text-sm text-fg disabled:opacity-50"
            placeholder="Capture discovery, objections, and next steps…"
          />
          <button
            type="button"
            disabled={converted || savingNotes}
            onClick={handleSaveNotes}
            className="mt-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-fg hover:bg-primary-dark disabled:opacity-50"
          >
            {savingNotes ? 'Saving…' : 'Save notes'}
          </button>
        </div>

        <div className="mt-6 border-t border-border pt-4">
          <SequenceCard contactType="lead" contactId={lead.id} contactName={lead.full_name} />
        </div>

        <div className="mt-6 border-t border-border pt-4">
          <h2 className="text-sm font-semibold text-fg">Email activity</h2>
          <ContactEmailHistory
            leadId={lead.id}
            refreshKey={emailRefreshKey}
            onEditScheduled={(row) => {
              setEditScheduledEmail(row);
              setShowEmailModal(true);
            }}
          />
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setShowEmailSuggestionModal(true)}
              className="rounded-xl border border-border px-4 py-2 text-sm font-medium text-fg hover:bg-surface-muted disabled:opacity-50"
            >
              Generate Email Suggestion
            </button>
            <button
              type="button"
              onClick={() => {
                setEditScheduledEmail(null);
                setShowEmailModal(true);
              }}
              className="rounded-xl bg-primary px-4 py-2 text-sm font-medium text-primary-fg hover:bg-primary-dark"
            >
              Send email
            </button>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2 border-t border-border pt-4">
          <button
            type="button"
            disabled={converted}
            onClick={handleConvert}
            className="rounded-xl bg-primary px-4 py-2 text-sm font-medium text-primary-fg disabled:opacity-50"
          >
            Convert to prospect
          </button>
        </div>
      </div>

      {showEmailModal ? (
        <EmailComposeModal
          open={showEmailModal}
          onClose={() => {
            setShowEmailModal(false);
            setEditScheduledEmail(null);
          }}
          contact={lead}
          contactKind="lead"
          followUpContext="lead"
          editScheduledEmail={editScheduledEmail}
          onSent={() => setEmailRefreshKey((k) => k + 1)}
        />
      ) : null}
      {showEmailSuggestionModal ? (
        <EmailSuggestionModal
          open={showEmailSuggestionModal}
          onClose={() => setShowEmailSuggestionModal(false)}
          suggestion={null}
          leadId={lead.id}
          contextLabel={lead.company || lead.full_name}
        />
      ) : null}
    </div>
  );
};

export default LeadInboundDetail;
